import type { AxiosInstance, AxiosResponse } from 'axios'
import { BaseAPI } from './BaseAPI.js'
import type {
  JSONApiResourceBase,
  JSONApiResponse,
} from '../interfaces/JSONApi.js'
import type {
  QaAcknowledgement,
  QaAppeal,
  QaAppealQuestion,
  QaAssignment,
  QaAssignmentSetupInput,
  QaAssignmentSetupOptions,
  QaAssignmentTransitionInput,
  QaAuditEvent,
  QaCase,
  QaDisputeInput,
  QaDisputeResolutionInput,
  QaFinalizationInput,
  QaFinalizationQueueItem,
  QaFinalizationWorkspace,
  QaLanguage,
  QaOfficialResultAnswer,
  QaOfficialResultRevision,
  QaOrganizationSetting,
  QaOperationsFilters,
  QaOperationsOverview,
  QaOperationsWorkItemsResponse,
  QaReviewerAnswer,
  QaReviewerSubmission,
  QaReviewDraft,
  QaReviewDraftInput,
  QaReviewQueueItem,
  QaReviewSubmitInput,
  QaReviewWorkspace,
  QaResultQueueItem,
  QaResultWorkspace,
  QaRuleCampaign,
  QaRuleDisposition,
  QaRuleForecast,
  QaRuleRevision,
  QaSamplingRule,
  QaSamplingRuleHierarchy,
  QaSamplingRun,
  QaScorecard,
  QaScorecardCategory,
  QaScorecardHierarchy,
  QaScorecardImportDocument,
  QaScorecardImportFormat,
  QaScorecardImportPreview,
  QaScorecardImportPreviewInput,
  QaScorecardQuestion,
  QaScorecardReorderInput,
  QaScorecardVersion,
  QaTargetedSelectionInput,
  QaTargetedSelectionResult,
} from '../types/Qa.js'
import type { QaCaseCommand } from '@letsdial/qa-domain'

export class QaResourceAPI<
  ResourceType extends JSONApiResourceBase,
> extends BaseAPI<ResourceType> {
  constructor(
    axios: AxiosInstance,
    private readonly resourcePath: string,
  ) {
    super(axios)
  }

  override get resource(): string {
    return this.resourcePath
  }
}

async function postCommand<ResourceType extends JSONApiResourceBase>(
  client: AxiosInstance,
  path: string,
  body?: object,
): Promise<JSONApiResponse<ResourceType>> {
  const response: AxiosResponse<JSONApiResponse<ResourceType>> = body
    ? await client.post(path, body)
    : await client.post(path)

  return response.data
}

export class QaScorecardVersionsAPI extends QaResourceAPI<QaScorecardVersion> {
  constructor(axios: AxiosInstance) {
    super(axios, '/qaScorecardVersions')
  }

  public publish(id: number): Promise<JSONApiResponse<QaScorecardVersion>> {
    return postCommand(this._client, `${this.resource}/${id}/publish`)
  }

  public clone(id: number): Promise<JSONApiResponse<QaScorecardVersion>> {
    return postCommand(this._client, `${this.resource}/${id}/clone`)
  }

  public archive(id: number): Promise<JSONApiResponse<QaScorecardVersion>> {
    return postCommand(this._client, `${this.resource}/${id}/archive`)
  }

  public reorder(
    id: number,
    input: QaScorecardReorderInput,
  ): Promise<JSONApiResponse<QaScorecardVersion>> {
    return postCommand(this._client, `${this.resource}/${id}/reorder`, input)
  }
}

export class QaScorecardsAPI extends QaResourceAPI<QaScorecard> {
  constructor(axios: AxiosInstance) {
    super(axios, '/qaScorecards')
  }

  public async hierarchy(
    id: number,
  ): Promise<JSONApiResponse<QaScorecardHierarchy>> {
    const response = await this._client.get(`${this.resource}/${id}/hierarchy`)

    return response.data
  }

  public async effectiveVersion(
    id: number,
    at?: string,
  ): Promise<JSONApiResponse<QaScorecardVersion>> {
    const response = await this._client.get(
      `${this.resource}/${id}/effectiveVersion`,
      { params: at ? { at } : undefined },
    )

    return response.data
  }

  public async previewImport(
    input: QaScorecardImportPreviewInput,
  ): Promise<QaScorecardImportPreview> {
    const response = await this._client.post(
      `${this.resource}/import/preview`,
      input,
    )

    return response.data
  }

  public async commitImport(
    document: QaScorecardImportDocument,
  ): Promise<JSONApiResponse<QaScorecardHierarchy>> {
    const response = await this._client.post(`${this.resource}/import/commit`, {
      document,
    })

    return response.data
  }

  public async importTemplate(
    format: QaScorecardImportFormat,
    organizationId: number,
  ): Promise<ArrayBuffer> {
    const response = await this._client.get(
      `${this.resource}/import/template`,
      {
        params: { format, organizationId },
        responseType: 'arraybuffer',
      },
    )

    return response.data
  }
}

export class QaRuleRevisionsAPI extends QaResourceAPI<QaRuleRevision> {
  constructor(axios: AxiosInstance) {
    super(axios, '/qaRuleRevisions')
  }

  public activate(id: number): Promise<JSONApiResponse<QaRuleRevision>> {
    return postCommand(this._client, `${this.resource}/${id}/activate`)
  }

  public clone(id: number): Promise<JSONApiResponse<QaRuleRevision>> {
    return postCommand(this._client, `${this.resource}/${id}/clone`)
  }

  public deactivate(id: number): Promise<JSONApiResponse<QaRuleRevision>> {
    return postCommand(this._client, `${this.resource}/${id}/deactivate`)
  }

  public async forecast(id: number): Promise<QaRuleForecast> {
    const response = await this._client.get(`${this.resource}/${id}/forecast`)

    return response.data
  }
}

export class QaSamplingRulesAPI extends QaResourceAPI<QaSamplingRule> {
  constructor(axios: AxiosInstance) {
    super(axios, '/qaSamplingRules')
  }

  public async hierarchy(
    id: number,
  ): Promise<JSONApiResponse<QaSamplingRuleHierarchy>> {
    const response = await this._client.get(`${this.resource}/${id}/hierarchy`)

    return response.data
  }
}

export class QaCasesAPI extends QaResourceAPI<QaCase> {
  constructor(axios: AxiosInstance) {
    super(axios, '/qaCases')
  }

  public transition(
    id: number,
    command: QaCaseCommand,
  ): Promise<JSONApiResponse<QaCase>> {
    return postCommand(this._client, `${this.resource}/${id}/transition`, {
      command,
    })
  }

  public assignFinalizer(
    id: number,
    finalizerUserId: number,
  ): Promise<JSONApiResponse<QaCase>> {
    return postCommand(this._client, `${this.resource}/${id}/finalizer`, {
      finalizerUserId,
    })
  }

  public async createTargeted(
    input: QaTargetedSelectionInput,
  ): Promise<QaTargetedSelectionResult> {
    const response = await this._client.post(`${this.resource}/targeted`, input)

    return response.data
  }

  public async assignmentOptions(
    id: number,
  ): Promise<QaAssignmentSetupOptions> {
    const response = await this._client.get(
      `${this.resource}/${id}/assignment-options`,
    )

    return response.data
  }

  public assignmentSetup(
    id: number,
    input: QaAssignmentSetupInput,
  ): Promise<JSONApiResponse<QaCase>> {
    return postCommand(
      this._client,
      `${this.resource}/${id}/assignment-setup`,
      input,
    )
  }

  public async finalizationQueue(): Promise<QaFinalizationQueueItem[]> {
    const response = await this._client.get(
      `${this.resource}/finalization-queue`,
    )

    return response.data.data
  }

  public async finalizationWorkspace(
    id: number,
  ): Promise<QaFinalizationWorkspace> {
    const response = await this._client.get(
      `${this.resource}/${id}/finalization-workspace`,
    )

    return response.data
  }

  public finalize(
    id: number,
    input: QaFinalizationInput,
  ): Promise<JSONApiResponse<QaOfficialResultRevision>> {
    return postCommand(this._client, `${this.resource}/${id}/finalize`, input)
  }

  public async results(): Promise<QaResultQueueItem[]> {
    const response = await this._client.get(`${this.resource}/results`)

    return response.data.data
  }

  public async resultWorkspace(id: number): Promise<QaResultWorkspace> {
    const response = await this._client.get(
      `${this.resource}/${id}/result-workspace`,
    )

    return response.data
  }

  public dispute(
    id: number,
    input: QaDisputeInput,
  ): Promise<JSONApiResponse<QaAppeal>> {
    return postCommand(this._client, `${this.resource}/${id}/disputes`, input)
  }
}

export class QaAssignmentsAPI extends QaResourceAPI<QaAssignment> {
  constructor(axios: AxiosInstance) {
    super(axios, '/qaAssignments')
  }

  public transition(
    id: number,
    input: QaAssignmentTransitionInput,
  ): Promise<JSONApiResponse<QaAssignment>> {
    return postCommand(this._client, `${this.resource}/${id}/transition`, input)
  }

  public async myReviews(): Promise<QaReviewQueueItem[]> {
    const response = await this._client.get(`${this.resource}/my-reviews`)

    return response.data.data
  }

  public async reviewWorkspace(id: number): Promise<QaReviewWorkspace> {
    const response = await this._client.get(
      `${this.resource}/${id}/review-workspace`,
    )

    return response.data
  }

  public async saveReviewDraft(
    id: number,
    input: QaReviewDraftInput,
  ): Promise<QaReviewDraft> {
    const response = await this._client.put(
      `${this.resource}/${id}/review-draft`,
      input,
    )

    return response.data
  }

  public submitReview(
    id: number,
    input: QaReviewSubmitInput,
  ): Promise<JSONApiResponse<QaReviewerSubmission>> {
    return postCommand(
      this._client,
      `${this.resource}/${id}/review-submit`,
      input,
    )
  }
}

export class QaAppealsAPI extends QaResourceAPI<QaAppeal> {
  constructor(axios: AxiosInstance) {
    super(axios, '/qaAppeals')
  }

  public resolve(
    id: number,
    input: string | QaDisputeResolutionInput,
  ): Promise<JSONApiResponse<QaAppeal>> {
    const payload = typeof input === 'string' ? { resolution: input } : input

    return postCommand(this._client, `${this.resource}/${id}/resolve`, payload)
  }
}

export class QaOperationsAPI {
  constructor(private readonly client: AxiosInstance) {}

  public async overview(filters: QaOperationsFilters = {}): Promise<QaOperationsOverview> {
    const response = await this.client.get('/qaOperations/overview', { params: filters })

    return response.data.data
  }

  public async workItems(
    filters: QaOperationsFilters = {},
  ): Promise<QaOperationsWorkItemsResponse> {
    const response = await this.client.get('/qaOperations/work-items', { params: filters })

    return response.data
  }
}

export function createQaResources(axios: AxiosInstance) {
  return {
    qaOperations: new QaOperationsAPI(axios),
    qaOrganizationSettings: new QaResourceAPI<QaOrganizationSetting>(
      axios,
      '/qaOrganizationSettings',
    ),
    qaLanguages: new QaResourceAPI<QaLanguage>(axios, '/qaLanguages'),
    qaScorecards: new QaScorecardsAPI(axios),
    qaScorecardVersions: new QaScorecardVersionsAPI(axios),
    qaScorecardCategories: new QaResourceAPI<QaScorecardCategory>(
      axios,
      '/qaScorecardCategories',
    ),
    qaScorecardQuestions: new QaResourceAPI<QaScorecardQuestion>(
      axios,
      '/qaScorecardQuestions',
    ),
    qaSamplingRules: new QaSamplingRulesAPI(axios),
    qaRuleRevisions: new QaRuleRevisionsAPI(axios),
    qaRuleCampaigns: new QaResourceAPI<QaRuleCampaign>(
      axios,
      '/qaRuleCampaigns',
    ),
    qaRuleDispositions: new QaResourceAPI<QaRuleDisposition>(
      axios,
      '/qaRuleDispositions',
    ),
    qaSamplingRuns: new QaResourceAPI<QaSamplingRun>(axios, '/qaSamplingRuns'),
    qaCases: new QaCasesAPI(axios),
    qaAssignments: new QaAssignmentsAPI(axios),
    qaReviewerSubmissions: new QaResourceAPI<QaReviewerSubmission>(
      axios,
      '/qaReviewerSubmissions',
    ),
    qaReviewerAnswers: new QaResourceAPI<QaReviewerAnswer>(
      axios,
      '/qaReviewerAnswers',
    ),
    qaOfficialResultRevisions: new QaResourceAPI<QaOfficialResultRevision>(
      axios,
      '/qaOfficialResultRevisions',
    ),
    qaOfficialResultAnswers: new QaResourceAPI<QaOfficialResultAnswer>(
      axios,
      '/qaOfficialResultAnswers',
    ),
    qaAcknowledgements: new QaResourceAPI<QaAcknowledgement>(
      axios,
      '/qaAcknowledgements',
    ),
    qaAppeals: new QaAppealsAPI(axios),
    qaAppealQuestions: new QaResourceAPI<QaAppealQuestion>(
      axios,
      '/qaAppealQuestions',
    ),
    qaAuditEvents: new QaResourceAPI<QaAuditEvent>(axios, '/qaAuditEvents'),
  }
}
